import { FC, useCallback, useMemo, useRef } from 'react';
import { Typography } from '@mui/material';
import { getCoreRowModel, getFilteredRowModel, SortingState, Updater, useReactTable } from '@tanstack/react-table';
import { mergeSx } from 'merge-sx';
import { TotalLanguagesRowDto } from '@families-link/shared';

import { CoreModal, CoreModalProps } from '../../../_core/components/_ui/core-modal';
import { mainTitleModalWindowStyles } from '../../../_core/styles';
import { AnalyticsFilters } from '../../../_core/interfaces/analytics-filters.interface';
import { useGetAnalyticsTotalLanguagesQuery } from '../../../../store/api/analytics.api';
import { CoreTable, CoreTableEmptyBody } from '../../../_core/components/_ui/core-table';
import {
	TotalLanguagesColumnName,
	useTotalLanguagesTableColumns,
} from '../../hooks/table-columns/total-languages-table-columns.hook';
import { useSortingParams } from '../../../_core/hooks';

type TotalLanguagesModalProps = CoreModalProps & {
	filters: AnalyticsFilters;
	authToken?: string;
};

export const TotalLanguagesModal: FC<TotalLanguagesModalProps> = (props) => {
	const { filters, onClose, authToken, ...modalProps } = props;
	const { open } = modalProps as { open?: boolean };

	const { sortingArray, sortingParams, handleSortingChange } = useSortingParams();

	const { data, isFetching } = useGetAnalyticsTotalLanguagesQuery(
		{ queryParams: filters, sorting: sortingParams, authToken: authToken },
		{
			skip: !open,
			refetchOnMountOrArgChange: true,
		}
	);

	const tableData = useMemo<TotalLanguagesRowDto[]>(() => data ?? [], [data]);

	const tableColumns = useMemo<TotalLanguagesColumnName[]>(
		() => ['language', 'engagements', 'surveysSent', 'surveysCompleted'],
		[]
	);

	const totalRow = useMemo<TotalLanguagesRowDto>(
		() =>
			tableData.reduce(
				(accumulator: TotalLanguagesRowDto, row: TotalLanguagesRowDto) => {
					accumulator.engagements += row.engagements ?? 0;
					accumulator.surveysSent += row.surveysSent ?? 0;
					accumulator.surveysCompleted += row.surveysCompleted ?? 0;
					return accumulator;
				},
				{
					language: 'Total',
					engagements: 0,
					surveysSent: 0,
					surveysCompleted: 0,
				}
			),
		[data]
	);

	const totalData = useMemo<TotalLanguagesRowDto[]>(() => [totalRow, ...tableData], [totalRow, data]);

	const columns = useTotalLanguagesTableColumns(tableColumns);

	const table = useReactTable<TotalLanguagesRowDto>({
		data: totalData,
		columns,
		state: {
			sorting: sortingArray,
		},
		onSortingChange: handleSortingChange as (updater: Updater<SortingState>) => void,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getRowId: (row) => row.language,
	});

	return (
		<CoreModal
			{...modalProps}
			modalWidth={720}
			onClose={onClose}
			bodySx={{ p: 0, px: 5, mb: 5, overflow: 'hidden' }}
			paperSx={{ width: '100%' }}
		>
			<Typography
				variant="h5"
				sx={mergeSx(mainTitleModalWindowStyles, {
					position: 'sticky',
					top: 0,
					zIndex: 10,
					backgroundColor: (theme) => theme.colors.white,
				})}
			>
				Language Detailed Statistics
			</Typography>

			<CoreTable
				px={0}
				maxHeight="70vh"
				rowModel={table.getRowModel()}
				headerGroups={table.getHeaderGroups()}
				emptyListComponent={<CoreTableEmptyBody description={'No languages data found'} />}
			/>
		</CoreModal>
	);
};
